import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from 'hooks';
import { clientNav, clientNavBottom } from './navigation';
import { HugeiconsIcon } from '@hugeicons/react';
import icon from "../../assets/images/144x.png";

const SIDEBAR_KEY = 'cpay-client-sidebar';

function NavItem({ item, isActive, expanded }) {
    const [hovered, setHovered] = useState(false);

    return (
        <li
            className="relative"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Link
                to={item.path}
                title={item.text}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                        ? 'bg-amber-50 text-amber-700'
                        : 'text-gray-500 dark:text-white/80 hover:text-gray-800 hover:bg-gray-100'
                } ${!expanded ? 'justify-center' : ''}`}
            >
                <HugeiconsIcon
                    icon={item.icon}
                    size={20}
                    className="shrink-0"
                    color={isActive ? '#b45309' : 'currentColor'}
                />
                {expanded && <span className="truncate">{item.text}</span>}
            </Link>

            {/* Tooltip when collapsed */}
            {!expanded && hovered && (
                <div className="absolute left-full top-0 ml-2 z-50 bg-white rounded-lg shadow-xl border border-gray-100 px-3 py-2 whitespace-nowrap pointer-events-none">
                    <span className="text-sm font-medium text-gray-700">{item.text}</span>
                </div>
            )}
        </li>
    );
}

function Aside() {
    const { user } = useUser();
    const location = useLocation();
    const currentPath = location.pathname.split('/')[1];

    const [expanded, setExpanded] = useState(() => {
        const saved = localStorage.getItem(SIDEBAR_KEY);
        return saved === null ? true : saved === 'true';
    });

    useEffect(() => {
        localStorage.setItem(SIDEBAR_KEY, String(expanded));
    }, [expanded]);

    // Listen for toggle-sidebar event dispatched from Topbar
    useEffect(() => {
        const handler = () => setExpanded(e => !e);
        window.addEventListener('toggle-sidebar', handler);
        return () => window.removeEventListener('toggle-sidebar', handler);
    }, []);

    const visibleNav = (user?.isVerified && user?.idNumber)
        ? clientNav
        : clientNav.filter(link => link.active !== 'loans');

    const sidebarWidth = expanded ? 'w-56' : 'w-14';

    return (
        <>
        {/* Desktop sidebar */}
        <aside
            id='sidebar'
            aria-label='Sidebar'
            className={`hidden lg:flex flex-col shrink-0 h-full fixed left-0 top-0 bg-white dark:bg-dark-page border-r border-gray-200 dark:border-white/[.08] z-20 transition-all duration-200 ${sidebarWidth}`}
        >
            {/* Logo row — aligns with topbar height */}
            <div className={`flex items-center h-14 border-b border-gray-200 dark:border-white/[.08] shrink-0 overflow-hidden ${expanded ? 'gap-2.5 px-4' : 'justify-center px-2'}`}>
                <img src={icon} alt='CedisPay' className='w-7 h-7 rounded-full shrink-0' />
                {expanded && (
                    <span className='font-semibold text-sm text-gray-800 uppercase tracking-wide truncate'>CedisPay</span>
                )}
            </div>

            {/* Main nav */}
            <nav className='flex-1 px-2 py-4 overflow-y-auto overflow-x-visible'>
                <ul className='space-y-0.5'>
                    {visibleNav.map((item, index) => (
                        <NavItem
                            key={index}
                            item={item}
                            isActive={currentPath === item.active}
                            expanded={expanded}
                        />
                    ))}
                </ul>
            </nav>

            {/* Bottom nav (Settings) */}
            <div className='px-2 pb-4 border-t border-gray-100 dark:border-white/[.08] pt-2'>
                <ul className='space-y-0.5'>
                    {clientNavBottom.map((item, index) => (
                        <NavItem
                            key={index}
                            item={item}
                            isActive={currentPath === item.active}
                            expanded={expanded}
                        />
                    ))}
                </ul>
            </div>
        </aside>

        {/* Mobile sidebar drawer */}
        <aside id='sidebar-mobile' aria-label='Sidebar Mobile' className='mobile-menu hidden lg:hidden'>
            <div className='px-2 py-4'>
                <ul className='space-y-0.5'>
                    {visibleNav.map((item, index) => (
                        <NavItem key={index} item={item} isActive={currentPath === item.active} expanded={true} />
                    ))}
                </ul>
                <div className='border-t border-gray-100 mt-3 pt-3'>
                    <ul className='space-y-0.5'>
                        {clientNavBottom.map((item, index) => (
                            <NavItem key={index} item={item} isActive={currentPath === item.active} expanded={true} />
                        ))}
                    </ul>
                </div>
            </div>
        </aside>
        </>
    );
}

export default Aside;
